import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageProvider";
import { IMAGES } from "@/lib/brand";
import { CheckCircle2 } from "lucide-react";

const Section = ({ id, children, className = "", testid }) => (
  <section id={id} data-testid={testid} className={`relative scroll-mt-24 ${className}`}>
    {children}
  </section>
);

export const About = () => {
  const { t, lang } = useLang();
  return (
    <Section
      id="about"
      testid="about-section"
      className="overflow-hidden bg-[#F8FAFC] py-24 dark:bg-[#0B1020] md:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-[#1E3A8A]/15 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1E3A8A] dark:border-white/10 dark:bg-white/5 dark:text-[#A5B4FC]">
              {t.about.eyebrow}
            </div>
            <h2 className="mt-5 font-display text-[clamp(2rem,4.2vw,3.25rem)] font-bold leading-tight tracking-tight text-[#0F172A] dark:text-white">
              {t.about.title}
            </h2>
            <p className="mt-6 max-w-xl text-pretty text-base leading-relaxed text-slate-600 dark:text-slate-300 md:text-[17px]">
              {t.about.body}
            </p>
            <ul className="mt-8 space-y-3">
              {t.about.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-[#7C3AED]" />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200 md:text-base">
                    {b}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-3xl shadow-elevated ring-1 ring-black/5">
              <img src={IMAGES.about} alt="" className="h-[520px] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0F172A]/40 via-transparent to-[#7C3AED]/20" />
              {/* Floating stat card */}
              <div className="absolute bottom-5 left-5 right-5 glass-strong rounded-2xl p-5 shadow-elevated">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="font-display text-3xl font-bold text-[#0F172A] dark:text-white">98%</div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      {lang === "de" ? "Kundenzufriedenheit" : lang === "hr" ? "Zadovoljstvo klijenata" : lang === "hu" ? "Ügyfél-elégedettség" : "Customer satisfaction"}
                    </div>
                  </div>
                  <div className="h-12 w-px bg-slate-200 dark:bg-white/10" />
                  <div>
                    <div className="font-display text-3xl font-bold text-[#0F172A] dark:text-white">48h</div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      {lang === "de" ? "Antwortzeit" : lang === "hr" ? "Vrijeme odgovora" : lang === "hu" ? "Válaszidő" : "Response time"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Decorative accent */}
            <div className="absolute -top-6 -right-6 h-32 w-32 -z-10 rounded-3xl bg-gradient-to-br from-[#2563EB] to-[#7C3AED] opacity-30 blur-2xl" />
          </motion.div>
        </div>
      </div>
    </Section>
  );
};

export default About;
